import { createFileRoute, getRouteApi, useRouter } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import { t } from '@lingui/core/macro';
import { settingsService } from '@/services/settings';
import { useToast } from '@/hooks/useToast';
import { showFlashMessage } from '@/utils/flashMessageUtils';
import { submitStripeCheckout } from '@/utils/stripeCheckout';
import PremiumSettings from '@/components/Settings/PremiumSettings';

const settingsRoute = getRouteApi('/_app/settings');

export const Route = createFileRoute('/_app/settings/premium')({
  component: PremiumSettingsPage,
});

function PremiumSettingsPage() {
  const router = useRouter();
  const settings = settingsRoute.useLoaderData();
  const { showToast } = useToast();
  const formKey = settings.user.formKey;

  // Unsubscribe mutation
  const unsubscribeMutation = useMutation({
    mutationFn: (formKey: string) => settingsService.unsubscribeStripe(formKey),
    onSuccess: async () => {
      // Check for flash message from backend (with delay for fetch redirects)
      showFlashMessage(showToast);
      await router.invalidate();
    },
    onError: (error) => {
      showToast(
        error instanceof Error
          ? error.message
          : t`Sorry, there was an error. Please try again.`,
        'error',
      );
    },
  });

  // Enable autorenew mutation
  const enableAutorenewMutation = useMutation({
    mutationFn: (formKey: string) => settingsService.enableStripeAutorenew(formKey),
    onSuccess: async () => {
      // Check for flash message from backend (with delay for fetch redirects)
      showFlashMessage(showToast);
      await router.invalidate();
    },
    onError: (error) => {
      showToast(
        error instanceof Error
          ? error.message
          : t`Sorry, there was an error. Please try again.`,
        'error',
      );
    },
  });

  const handleSubscribe = (plan: 'monthly' | 'yearly') => {
    submitStripeCheckout({
      plan,
      formKey,
      successUrl: `${window.location.origin}/settings/premium`,
    });
  };

  const handleUpdateCreditCard = () => {
    // Stripe update redirects to external Stripe URL
    // We need to use form submission for this
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = '/user/stripe/update';

    const formKeyInput = document.createElement('input');
    formKeyInput.type = 'hidden';
    formKeyInput.name = 'form_key';
    formKeyInput.value = formKey;
    form.appendChild(formKeyInput);

    const successUrlInput = document.createElement('input');
    successUrlInput.type = 'hidden';
    successUrlInput.name = 'success_url';
    successUrlInput.value = `${window.location.origin}/settings/premium`;
    form.appendChild(successUrlInput);

    document.body.appendChild(form);
    form.submit();
  };

  return (
    <PremiumSettings
      user={settings.user}
      onSubscribe={handleSubscribe}
      onUpdateCreditCard={handleUpdateCreditCard}
      onUnsubscribe={() => {
        if (
          confirm(t`Are you sure you want to cancel your Instapaper Premium membership?`)
        ) {
          unsubscribeMutation.mutate(formKey);
        }
      }}
      isUnsubscribing={unsubscribeMutation.isPending}
      onEnableAutorenew={() => {
        enableAutorenewMutation.mutate(formKey);
      }}
      isEnablingAutorenew={enableAutorenewMutation.isPending}
    />
  );
}
